import React, { useState, useEffect } from "react";
import {
  FaUserEdit,
  FaLock,
  FaEnvelope,
  FaUser,
  FaMoneyBillWave,
  FaLink,
  FaUsers,
  FaCheckCircle,
  FaShieldAlt,
  FaCalendarAlt,
  FaArrowLeft,
  FaEye,
  FaEyeSlash,
  FaSun,
  FaMoon,
} from "react-icons/fa";
import Layout from "../../Component/Layout/Layout";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../Context/auth";
import { useTheme } from "../../Context/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";

const Profile = () => {
  const [auth] = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [changePasswordMode, setChangePasswordMode] = useState(false);
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [formData, setFormData] = useState({
    userName: "",
    email: "",
    TotalEarnings: 0,
    earnings: 0,
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Fetch user data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(
          "https://bioopay-full-backend.onrender.com/api/v1/users/me",
          {
            headers: {
              Authorization: `Bearer ${auth.token}`,
            },
          }
        );
        setUser(response.data.user);
        setFormData({
          userName: response.data.user.userName,
          email: response.data.user.email,
          TotalEarnings: response.data.user.TotalEarnings,
          earnings: response.data.user.earnings,
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user profile:", error);
        toast.error("Failed to load profile data");
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [auth.token]);

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(
        "https://bioopay-full-backend.onrender.com/api/v1/users/update",
        formData,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );
      setUser(response.data.user);
      setEditMode(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    try {
      await axios.put(
        "https://bioopay-full-backend.onrender.com/api/v1/users/change-password",
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setChangePasswordMode(false);
      toast.success("Password changed successfully!");
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(error.response?.data?.message || "Failed to change password");
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword({
      ...showPassword,
      [field]: !showPassword[field],
    });
  };

  // Main styles with dark mode support
  const styles = {
    pageContainer: {
      minHeight: "100vh",
      backgroundColor: darkMode ? "#121212" : "#f8f9fa",
      padding: "30px 15px",
      transition: "background-color 0.3s ease",
    },
    mainContainer: {
      maxWidth: "900px",
      margin: "0 auto",
    },
    profileCard: {
      backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
      borderRadius: "16px",
      boxShadow: darkMode
        ? "0 10px 30px rgba(0, 0, 0, 0.3)"
        : "0 10px 30px rgba(0, 0, 0, 0.08)",
      overflow: "hidden",
      transition: "all 0.3s ease",
    },
    header: {
      background: darkMode
        ? "linear-gradient(135deg, #4b0082, #6a0dad)"
        : "linear-gradient(135deg, #6d28d9, #8b5cf6)",
      color: "white",
      padding: "30px",
      textAlign: "center",
      position: "relative",
    },
    avatar: {
      width: "120px",
      height: "120px",
      borderRadius: "50%",
      border: "4px solid rgba(255, 255, 255, 0.3)",
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 15px",
      fontSize: "48px",
      fontWeight: "bold",
      color: "white",
    },
    userName: {
      fontSize: "28px",
      fontWeight: "700",
      margin: "0 0 5px",
      color: "white",
    },
    badgeContainer: {
      display: "flex",
      gap: "10px",
      justifyContent: "center",
      marginTop: "15px",
    },
    badge: {
      padding: "5px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "600",
      textTransform: "uppercase",
    },
    activeBadge: {
      backgroundColor: darkMode
        ? "rgba(16, 185, 129, 0.3)"
        : "rgba(16, 185, 129, 0.2)",
      color: "#10b981",
    },
    adminBadge: {
      backgroundColor: darkMode
        ? "rgba(107, 114, 128, 0.3)"
        : "rgba(107, 114, 128, 0.2)",
      color: "#6b7280",
    },
    verifiedBadge: {
      backgroundColor: darkMode
        ? "rgba(59, 130, 246, 0.3)"
        : "rgba(59, 130, 246, 0.2)",
      color: "#3b82f6",
    },
    contentSection: {
      padding: "25px",
    },
    infoCard: {
      backgroundColor: darkMode ? "#2a2a2a" : "#ffffff",
      borderRadius: "12px",
      padding: "20px",
      marginBottom: "20px",
      boxShadow: darkMode
        ? "0 4px 12px rgba(0, 0, 0, 0.2)"
        : "0 4px 12px rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s ease",
    },
    sectionTitle: {
      fontSize: "20px",
      fontWeight: "600",
      marginBottom: "20px",
      color: darkMode ? "#e0e0e0" : "#374151",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    icon: {
      color: darkMode ? "#bb86fc" : "#7c3aed",
      fontSize: "20px",
      marginRight: "12px",
    },
    detailItem: {
      display: "flex",
      alignItems: "center",
      marginBottom: "15px",
      fontSize: "16px",
      color: darkMode ? "#b0b0b0" : "#4b5563",
    },
    actionButton: {
      padding: "10px 20px",
      backgroundColor: darkMode ? "#bb86fc" : "#7c3aed",
      color: "white",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.3s ease",
    },
    secondaryButton: {
      padding: "10px 20px",
      backgroundColor: darkMode ? "#5c5c5c" : "#6b7280",
      color: "white",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.3s ease",
    },
    inputField: {
      width: "100%",
      padding: "12px 15px",
      border: darkMode ? "1px solid #444" : "1px solid #e5e7eb",
      borderRadius: "8px",
      fontSize: "15px",
      marginBottom: "15px",
      boxSizing: "border-box",
      transition: "all 0.3s ease",
      backgroundColor: darkMode ? "#333" : "#fff",
      color: darkMode ? "#e0e0e0" : "#111",
    },
    inputLabel: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "500",
      color: darkMode ? "#b0b0b0" : "#4b5563",
      fontSize: "14px",
    },
    passwordInputContainer: {
      position: "relative",
      width: "100%",
    },
    passwordToggle: {
      position: "absolute",
      right: "10px",
      top: "12px",
      cursor: "pointer",
      color: darkMode ? "#888" : "#9ca3af",
    },
    buttonGroup: {
      display: "flex",
      gap: "15px",
      marginTop: "20px",
    },
    footer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "15px 25px",
      borderTop: darkMode ? "1px solid #444" : "1px solid #e5e7eb",
      backgroundColor: darkMode ? "#252525" : "#f9fafb",
      fontSize: "14px",
      color: darkMode ? "#888" : "#6b7280",
    },
    backButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      color: "white",
      backgroundColor: "transparent",
      border: "1px solid rgba(255, 255, 255, 0.5)",
      borderRadius: "8px",
      padding: "8px 15px",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    themeToggle: {
      position: "absolute",
      top: "20px",
      left: "20px",
      background: "rgba(255, 255, 255, 0.2)",
      border: "none",
      borderRadius: "50%",
      width: "40px",
      height: "40px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "white",
      fontSize: "18px",
    },
    referralCode: {
      backgroundColor: darkMode ? "rgba(187, 134, 252, 0.2)" : "#ede9fe",
      color: darkMode ? "#bb86fc" : "#6d28d9",
      padding: "4px 8px",
      borderRadius: "6px",
      marginLeft: "8px",
      fontWeight: "500",
    },
    loadingSpinner: {
      width: "50px",
      height: "50px",
      border: `5px solid ${darkMode ? "#333" : "#f3f3f3"}`,
      borderTop: `5px solid ${darkMode ? "#bb86fc" : "#6d28d9"}`,
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.pageContainer}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          >
            <div style={styles.loadingSpinner}></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div style={styles.pageContainer}>
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
            }}
          >
            <p
              style={{
                color: "#ef4444",
                fontSize: "18px",
              }}
            >
              Failed to load user data
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={"BIooPay -UserProfile"}>
      <div style={styles.pageContainer}>
        <div style={styles.mainContainer}>
          <motion.div
            style={styles.profileCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header Section */}
            <div style={styles.header}>
              <button style={styles.themeToggle} onClick={toggleTheme}>
                {darkMode ? <FaSun /> : <FaMoon />}
              </button>

              <div
                style={{
                  position: "absolute",
                  top: "20px",
                  right: "20px",
                }}
              >
                {editMode || changePasswordMode ? (
                  <motion.button
                    style={styles.backButton}
                    onClick={() => {
                      setEditMode(false);
                      setChangePasswordMode(false);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaArrowLeft /> Back
                  </motion.button>
                ) : (
                  <motion.button
                    style={styles.actionButton}
                    onClick={() => setEditMode(true)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaUserEdit /> Edit Profile
                  </motion.button>
                )}
              </div>

              <motion.div
                style={styles.avatar}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                {user.userName.charAt(0).toUpperCase()}
              </motion.div>
              <motion.h1
                style={styles.userName}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {user.userName}
              </motion.h1>

              <motion.div
                style={styles.badgeContainer}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <span style={{ ...styles.badge, ...styles.activeBadge }}>
                  {user.accountStatus}
                </span>
                {user.role === "admin" && (
                  <span style={{ ...styles.badge, ...styles.adminBadge }}>
                    {user.role}
                  </span>
                )}
                {user.isVerified && (
                  <span style={{ ...styles.badge, ...styles.verifiedBadge }}>
                    Verified
                  </span>
                )}
              </motion.div>
            </div>

            {/* Main Content */}
            <div style={styles.contentSection}>
              <AnimatePresence mode="wait">
                {!editMode && !changePasswordMode && (
                  <motion.div
                    key="profile-view"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Personal Information */}
                    <motion.div style={styles.infoCard} whileHover={{ y: -5 }}>
                      <h3 style={styles.sectionTitle}>
                        <FaUser style={styles.icon} /> Personal Information
                      </h3>
                      <div style={styles.detailItem}>
                        <FaEnvelope style={styles.icon} />
                        <span>{user.email}</span>
                      </div>
                      <div style={styles.detailItem}>
                        <FaMoneyBillWave style={styles.icon} />
                        <span>
                          Total Earnings: ${user.TotalEarnings.toFixed(2)}
                        </span>
                      </div>
                      <div style={styles.detailItem}>
                        <FaMoneyBillWave style={styles.icon} />
                        <span>
                          Available Balance: ${user.earnings.toFixed(2)}
                        </span>
                      </div>
                    </motion.div>

                    {/* Referral Information */}
                    <motion.div style={styles.infoCard} whileHover={{ y: -5 }}>
                      <h3 style={styles.sectionTitle}>
                        <FaLink style={styles.icon} /> Referral Information
                      </h3>
                      <div style={styles.detailItem}>
                        <FaUsers style={styles.icon} />
                        <span>Total Referrals: {user.totelreffered}</span>
                      </div>
                      <div style={styles.detailItem}>
                        <FaLink style={styles.icon} />
                        <span>
                          Your Referral Code:
                          <span style={styles.referralCode}>
                            {user.referralCode}
                          </span>
                        </span>
                      </div>
                      <div style={styles.detailItem}>
                        <FaLink style={styles.icon} />
                        <span>
                          Referral Link:{" "}
                          <a
                            href={user.referralLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: darkMode ? "#bb86fc" : "#7c3aed",
                              textDecoration: "none",
                              wordBreak: "break-all",
                            }}
                          >
                            {user.referralLink}
                          </a>
                        </span>
                      </div>
                    </motion.div>

                    {/* Account Information */}
                    <motion.div style={styles.infoCard} whileHover={{ y: -5 }}>
                      <h3 style={styles.sectionTitle}>
                        <FaCalendarAlt style={styles.icon} /> Account
                        Information
                      </h3>
                      <div style={styles.detailItem}>
                        <FaShieldAlt style={styles.icon} />
                        <span>
                          Last Login:{" "}
                          {new Date(user.lastLoginAt).toLocaleString()}
                        </span>
                      </div>
                      <div style={styles.detailItem}>
                        <FaCalendarAlt style={styles.icon} />
                        <span>
                          Member Since:{" "}
                          {new Date(user.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </motion.div>

                    {/* Change Password Button */}
                    <div style={{ textAlign: "center", marginTop: "20px" }}>
                      <motion.button
                        style={styles.secondaryButton}
                        onClick={() => setChangePasswordMode(true)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FaLock /> Change Password
                      </motion.button>
                    </div>
                  </motion.div>
                )}

                {/* Edit Profile Form */}
                {editMode && (
                  <motion.div
                    key="edit-form"
                    style={styles.infoCard}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3 style={styles.sectionTitle}>
                      <FaUserEdit style={styles.icon} /> Edit Profile
                    </h3>
                    <form onSubmit={handleProfileUpdate}>
                      <label style={styles.inputLabel}>Username</label>
                      <input
                        type="text"
                        style={styles.inputField}
                        value={formData.userName}
                        onChange={(e) =>
                          setFormData({ ...formData, userName: e.target.value })
                        }
                        required
                      />

                      <label style={styles.inputLabel}>Email</label>
                      <input
                        type="email"
                        style={{
                          ...styles.inputField,
                          backgroundColor: darkMode ? "#444" : "#f3f4f6",
                        }}
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        required
                        disabled
                      />

                      <div style={styles.buttonGroup}>
                        <motion.button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={() => setEditMode(false)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          type="submit"
                          style={styles.actionButton}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Save Changes
                        </motion.button>
                      </div>
                    </form>
                  </motion.div>
                )}

                {/* Change Password Form */}
                {changePasswordMode && (
                  <motion.div
                    key="password-form"
                    style={styles.infoCard}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3 style={styles.sectionTitle}>
                      <FaLock style={styles.icon} /> Change Password
                    </h3>
                    <form onSubmit={handlePasswordChange}>
                      <label style={styles.inputLabel}>Current Password</label>
                      <div style={styles.passwordInputContainer}>
                        <input
                          type={showPassword.current ? "text" : "password"}
                          style={styles.inputField}
                          value={passwordData.currentPassword}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              currentPassword: e.target.value,
                            })
                          }
                          required
                        />
                        <span
                          style={styles.passwordToggle}
                          onClick={() => togglePasswordVisibility("current")}
                        >
                          {showPassword.current ? <FaEyeSlash /> : <FaEye />}
                        </span>
                      </div>

                      <label style={styles.inputLabel}>New Password</label>
                      <div style={styles.passwordInputContainer}>
                        <input
                          type={showPassword.new ? "text" : "password"}
                          style={styles.inputField}
                          value={passwordData.newPassword}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              newPassword: e.target.value,
                            })
                          }
                          required
                          minLength="6"
                        />
                        <span
                          style={styles.passwordToggle}
                          onClick={() => togglePasswordVisibility("new")}
                        >
                          {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                        </span>
                      </div>

                      <label style={styles.inputLabel}>
                        Confirm New Password
                      </label>
                      <div style={styles.passwordInputContainer}>
                        <input
                          type={showPassword.confirm ? "text" : "password"}
                          style={styles.inputField}
                          value={passwordData.confirmPassword}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              confirmPassword: e.target.value,
                            })
                          }
                          required
                          minLength="6"
                        />
                        <span
                          style={styles.passwordToggle}
                          onClick={() => togglePasswordVisibility("confirm")}
                        >
                          {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                        </span>
                      </div>

                      <div style={styles.buttonGroup}>
                        <motion.button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={() => setChangePasswordMode(false)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          type="submit"
                          style={styles.actionButton}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Change Password
                        </motion.button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div style={styles.footer}>
              <span>
                Last updated: {new Date(user.updatedAt).toLocaleString()}
              </span>
              <span style={{ display: "flex", alignItems: "center" }}>
                <FaCheckCircle
                  style={{ color: "#10b981", marginRight: "5px" }}
                />
                Secure Connection
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
